import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { galleryImages } from "@/mocks/gallery";

// ノーマルカテゴリの画像のみ使用
const heroSlides = galleryImages.filter((img) => img.category === "normal");

export default function HeroSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const goToNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % heroSlides.length);
      setIsTransitioning(false);
    }, 600);
  }, [isTransitioning]);

  const goToPrev = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
      setIsTransitioning(false);
    }, 600);
  }, [isTransitioning]);

  // 自動スライド
  useEffect(() => {
    const timer = setInterval(goToNext, 4000);
    return () => clearInterval(timer);
  }, [goToNext]);

  const handleBooking = () => {
    if (window.innerWidth < 768 && location.pathname === "/") {
      const el = document.getElementById("contact");
      if (el) { el.scrollIntoView({ behavior: "smooth" }); return; }
    }
    navigate("/reservation");
  };

  const scrollToAbout = () => {
    const el = document.getElementById("about");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="min-h-screen flex flex-col items-center justify-center bg-stone-50 pt-14 md:pt-28 pb-6 px-4">

      {/* ===== タイトル エリア（文字のみ） ===== */}
      <div className="text-center mb-4 md:mb-5">
        {/* サブタイトル */}
        <p
          className="text-amber-800 text-xs md:text-sm tracking-[0.4em] mb-2 font-light"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
        >
          SM・M性感 個人セッション
        </p>

        {/* メインタイトル */}
        <h1
          className="text-stone-900 leading-none mb-2"
          style={{
            fontFamily: "'Noto Serif JP', serif",
            fontSize: "clamp(1.6rem, 4vw, 3rem)",
            fontWeight: 200,
            letterSpacing: "0.25em",
          }}
        >
          むぎ茶
        </h1>

        {/* ボトムライン */}
        <div className="flex items-center justify-center gap-4 mb-2">
          <div className="w-8 h-px bg-amber-500/80"></div>
          <div className="w-1.5 h-1.5 bg-amber-500/80 rotate-45"></div>
          <div className="w-8 h-px bg-amber-500/80"></div>
        </div>

        {/* 英語クレジット */}
        <p
          className="text-stone-500 text-xs md:text-sm tracking-[0.45em] font-light"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          By Mugi-cha production
        </p>
      </div>

      {/* ===== ギャラリースライドショー ===== */}
      <div className="w-full max-w-4xl mx-auto mb-4 md:mb-5">
        {/* メインスライダー */}
        <div className="relative overflow-hidden">
          {/* 複数画像を横並び表示 */}
          <div className="flex items-center justify-center gap-3 md:gap-4">
            {/* メイン画像（横長・全幅） */}
            <div
              className="relative w-full"
              style={{ aspectRatio: "16/9" }}
            >
              <img
                src={heroSlides[currentIndex].src}
                alt={heroSlides[currentIndex].alt}
                className={`w-full h-full object-cover object-center transition-opacity ${
                  isTransitioning ? "opacity-0" : "opacity-100"
                }`}
                style={{ transitionDuration: "600ms" }}
              />
            </div>
          </div>

          {/* スワイプ操作 */}
          <div
            className="absolute inset-0"
            onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
            onTouchMove={(e) => { touchEndX.current = e.touches[0].clientX; }}
            onTouchEnd={() => {
              if (touchStartX.current === null || touchEndX.current === null) return;
              const diff = touchStartX.current - touchEndX.current;
              if (Math.abs(diff) > 40) {
                if (diff > 0) goToNext();
                else goToPrev();
              }
              touchStartX.current = null;
              touchEndX.current = null;
            }}
          />

          {/* 矢印ボタン */}
          <button
            onClick={goToPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-700 transition-colors duration-200 cursor-pointer"
            aria-label="前の画像"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-700 transition-colors duration-200 cursor-pointer"
            aria-label="次の画像"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>

        {/* ドットインジケーター */}
        <div className="flex justify-center gap-1.5 mt-3">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                if (!isTransitioning) {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setCurrentIndex(i);
                    setIsTransitioning(false);
                  }, 400);
                }
              }}
              className={`transition-all duration-300 cursor-pointer ${
                i === currentIndex
                  ? "w-6 h-1 bg-amber-500"
                  : "w-1 h-1 bg-stone-300 hover:bg-stone-500"
              }`}
              aria-label={`画像 ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* ===== CTAボタン ===== */}
      <div className="flex flex-col sm:flex-row gap-3.5 items-stretch sm:items-center w-full sm:w-auto px-4 sm:px-0">
        <button
          onClick={handleBooking}
          className="group flex items-center justify-center gap-3 px-8 py-4 bg-amber-600 active:bg-amber-700 text-white text-[15px] tracking-[0.2em] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap rounded-lg active:scale-[0.97]"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-calendar-check-line text-base"></i>
          </div>
          ご予約はこちら
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1"></i>
          </div>
        </button>

        <button
          onClick={scrollToAbout}
          className="flex items-center justify-center gap-2.5 px-8 py-4 border-2 border-stone-300 active:border-stone-500 text-stone-500 active:text-stone-800 text-[15px] tracking-[0.2em] font-light transition-all duration-200 cursor-pointer whitespace-nowrap rounded-lg active:scale-[0.97] active:bg-stone-50"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-user-heart-line text-base"></i>
          </div>
          むぎ茶について
        </button>
      </div>

      {/* おはなしコースへの誘導リンク */}
      <div className="mt-5 flex flex-col items-center gap-2.5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-px bg-stone-200"></div>
          <span className="text-[11px] tracking-[0.35em] text-stone-300 uppercase">or</span>
          <div className="w-12 h-px bg-stone-200"></div>
        </div>
        <button
          onClick={() => {
            const el = document.getElementById("price");
            if (el) el.scrollIntoView({ behavior: "smooth" });
            setTimeout(() => {
              const talkEl = document.querySelector("[data-section='talk-course']");
              if (talkEl) talkEl.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 600);
          }}
          className="group flex flex-col items-center gap-2 px-6 py-3.5 border-2 border-stone-100 active:border-amber-300/60 bg-transparent active:bg-amber-50/50 rounded-xl transition-all duration-200 cursor-pointer active:scale-[0.97]"
        >
          <div className="flex items-center gap-3 text-stone-500 group-hover:text-amber-600 active:text-amber-600 transition-colors duration-200">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-chat-heart-line text-base"></i>
            </div>
            <span className="text-[15px] tracking-[0.25em] font-light">おはなしコース</span>
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-chat-heart-line text-base opacity-0"></i>
            </div>
          </div>
          <span className="text-[12px] tracking-[0.12em] text-stone-400 group-hover:text-amber-500/80 active:text-amber-500/80 transition-colors duration-200 whitespace-nowrap">カウンセリングセッションのご用意もあります</span>
          <div className="w-4 h-4 flex items-center justify-center text-stone-300 group-hover:text-amber-400 active:text-amber-400 mt-0.5 transition-all duration-300 group-hover:translate-y-0.5">
            <i className="ri-arrow-down-line text-sm"></i>
          </div>
        </button>
      </div>

    </section>
  );
}